import produce from 'immer';
import {
  AssignmentCustomerResponse,
  AssignmentDateContextResponse,
  AssignmentDateListItemResponse,
  AssignmentDateTimestampListItemResponse,
  AssignmentPspResponse,
  CommentResponse,
  CustomerAssignmentFilterListsResponse,
  CustomerAssignmentListItemResponse,
  CustomerProfileAssignmentItemResponse,
  CustomerProfileRatingListResponse,
  CustomerResponse,
  PaginatedApiResponse,
  PspAssignmentListItemResponse,
  PspAssignmentFilterListsResponse,
  PaginatedApiResponseWithFilters,
  CustomerDepartmentQualificationsListItemResponse,
  CustomerDepartmentResponse,
} from '../../../api';
import { BaseDateService } from '../../shared/services/base-date.service';
import { baseAssignmentsActions } from '../actions';

export interface AssignmentsState {
  list: {
    data:
      | (PaginatedApiResponseWithFilters & {
          data?: Array<PspAssignmentListItemResponse> | Array<CustomerAssignmentListItemResponse>;
          filters?: PspAssignmentFilterListsResponse | CustomerAssignmentFilterListsResponse;
        })
      | undefined;
    ok: boolean | undefined;
  };
  allCustomerAssignments: {
    data:
      | (PaginatedApiResponse & {
          data?: CustomerAssignmentListItemResponse[];
          filters?: CustomerAssignmentFilterListsResponse;
        })
      | undefined;
    ok: boolean | undefined;
  };
  assignment: {
    data: AssignmentPspResponse | AssignmentCustomerResponse | undefined;
    ok: boolean | undefined;
  };
  dates: {
    data: AssignmentDateListItemResponse[] | undefined;
    ok: boolean | undefined;
  };
  customer: {
    data: CustomerResponse | undefined;
    ok: boolean | undefined;
  };
  downloadDocument: {
    data: Blob | undefined;
    fileName?: string | undefined;
  };
  endedAssignments: {
    data: Array<CustomerProfileAssignmentItemResponse> | undefined;
    ok: boolean | undefined;
  };
  candidateEndedAssignments: {
    data: Array<CustomerProfileAssignmentItemResponse> | undefined;
    ok: boolean | undefined;
  };
  reviews: {
    data: CustomerProfileRatingListResponse | undefined;
    ok: boolean | undefined;
  };
  signContractStatus: {
    ok: boolean | undefined;
  };
  assignmentProfileIDCard: {
    data: Blob | undefined;
    fileName: string | undefined;
  };
  workCouncilComments: {
    data: CommentResponse[] | undefined;
    ok: boolean | undefined;
  };
  upcomingEPAssignments: {
    data: {
      assignments: PspAssignmentListItemResponse[] | CustomerAssignmentListItemResponse[] | undefined;
      totalCount: number | undefined;
    };
    ok: boolean | undefined;
  };
  upcomingMLPAssignments: {
    data: {
      assignments: PspAssignmentListItemResponse[] | CustomerAssignmentListItemResponse[] | undefined;
      totalCount: number | undefined;
    };
    ok: boolean | undefined;
  };
  locationDepartmentQualificationList: {
    data: CustomerDepartmentQualificationsListItemResponse[] | undefined;
    ok: boolean | undefined;
  };
  customerLocationDepartment: {
    data: CustomerDepartmentResponse | undefined;
    ok: boolean | undefined;
  };
  assignmentDateList: {
    data: string[] | undefined;
    ok: boolean | undefined;
  };
  assignmentDateTimestampList: {
    data:
      | {
          assignmentDate?: AssignmentDateContextResponse;
          timestamps?: AssignmentDateTimestampListItemResponse[];
        }
      | undefined;
    ok: boolean | undefined;
  };
}

export const ASSIGNMENTS_INITIAL_STATE: AssignmentsState = {
  list: {
    data: undefined,
    ok: undefined,
  },
  allCustomerAssignments: {
    data: undefined,
    ok: undefined,
  },
  assignment: {
    data: undefined,
    ok: undefined,
  },
  dates: {
    data: undefined,
    ok: undefined,
  },
  customer: {
    data: undefined,
    ok: undefined,
  },
  downloadDocument: {
    data: undefined,
    fileName: undefined,
  },
  endedAssignments: {
    data: undefined,
    ok: undefined,
  },
  candidateEndedAssignments: {
    data: undefined,
    ok: undefined,
  },
  reviews: {
    data: undefined,
    ok: undefined,
  },
  signContractStatus: {
    ok: undefined,
  },
  assignmentProfileIDCard: {
    data: undefined,
    fileName: undefined,
  },
  workCouncilComments: {
    data: undefined,
    ok: undefined,
  },
  upcomingEPAssignments: {
    data: {
      assignments: undefined,
      totalCount: undefined,
    },
    ok: undefined,
  },
  upcomingMLPAssignments: {
    data: {
      assignments: undefined,
      totalCount: undefined,
    },
    ok: undefined,
  },
  locationDepartmentQualificationList: {
    data: undefined,
    ok: undefined,
  },
  customerLocationDepartment: {
    data: undefined,
    ok: undefined,
  },
  assignmentDateList: {
    data: undefined,
    ok: undefined,
  },
  assignmentDateTimestampList: {
    data: undefined,
    ok: undefined,
  },
};

export function BaseAssignmentsReducer(state: AssignmentsState = ASSIGNMENTS_INITIAL_STATE, action: any): any {
  return produce(state, (draftState) => {
    switch (action.type) {
      // Assignment list
      case baseAssignmentsActions.loadAssignmentList.type:
        draftState.list.ok = undefined;
        draftState.list.data = undefined;
        return;

      case baseAssignmentsActions.setAssignmentList.type:
        draftState.list.data = action.payload;
        draftState.list.ok = true;
        return;

      case baseAssignmentsActions.errorAssignmentList.type:
        draftState.list.ok = false;
        return;

      // Assignment details
      case baseAssignmentsActions.loadAssignment.type:
        draftState.assignment = { data: undefined, ok: undefined };
        return;

      case baseAssignmentsActions.setAssignment.type:
        draftState.assignment.data = action.payload;
        return;

      case baseAssignmentsActions.successLoadAssignment.type:
        draftState.assignment.ok = true;
        return;

      case baseAssignmentsActions.errorLoadAssignment.type:
        draftState.assignment.ok = false;
        return;

      case baseAssignmentsActions.resetAssignment.type:
        draftState.assignment = ASSIGNMENTS_INITIAL_STATE.assignment;
        return;

      // Customer all assignments
      case baseAssignmentsActions.loadCustomerAllAssignments.type:
        draftState.allCustomerAssignments.ok = undefined;
        return;

      case baseAssignmentsActions.setCustomerAllAssignments.type:
        draftState.allCustomerAssignments.data = action.payload;
        return;

      case baseAssignmentsActions.successCustomerAllAssignment.type:
        draftState.allCustomerAssignments.ok = true;
        return;

      case baseAssignmentsActions.errorCustomerAllAssignment.type:
        draftState.allCustomerAssignments.ok = false;
        return;

      // Assignment dates
      case baseAssignmentsActions.loadAssignmentDates.type:
        draftState.dates = { data: undefined, ok: undefined };
        return;

      case baseAssignmentsActions.setAssignmentDates.type:
        draftState.dates.data = action.payload;
        return;

      case baseAssignmentsActions.successAssignmentDates.type:
        draftState.dates.ok = true;
        return;

      case baseAssignmentsActions.errorAssignmentDates.type:
        draftState.dates.ok = false;
        return;

      // Document view
      case baseAssignmentsActions.setDocumentView.type:
        draftState.downloadDocument.data = action.payload.document;
        draftState.downloadDocument.fileName = action.payload.fileName;
        return;

      case baseAssignmentsActions.clearDocumentView.type:
        draftState.downloadDocument.data = undefined;
        draftState.downloadDocument.fileName = undefined;
        return;

      // Candidate ended assignments
      case baseAssignmentsActions.getCandidateEndedAssignments.type:
        draftState.candidateEndedAssignments.ok = undefined;
        return;

      case baseAssignmentsActions.loadCandidateEndedAssignments.type:
        draftState.candidateEndedAssignments.data = action.payload;
        return;

      case baseAssignmentsActions.loadCandidateEndedAssignmentsSuccess.type:
        draftState.candidateEndedAssignments.ok = true;
        return;

      case baseAssignmentsActions.loadCandidateEndedAssignmentsError.type:
        draftState.candidateEndedAssignments.ok = false;
        return;

      // Customer reviews
      case baseAssignmentsActions.getCustomerReviews.type:
        draftState.reviews.ok = undefined;
        return;

      case baseAssignmentsActions.loadCustomerReviews.type:
        draftState.reviews.data = action.payload;
        return;

      case baseAssignmentsActions.loadCustomerReviewsSuccessfully.type:
        draftState.reviews.ok = true;
        return;

      case baseAssignmentsActions.loadCustomerReviewsError.type:
        draftState.reviews.ok = false;
        return;

      case baseAssignmentsActions.clearCustomerReviews.type:
        draftState.reviews = ASSIGNMENTS_INITIAL_STATE.reviews;
        return;

      case baseAssignmentsActions.assignmentCancellationSuccess.type:
        draftState.assignment.data = {
          ...draftState.assignment.data,
          // @ts-ignore
          assignmentCancelation: {
            ...(draftState.assignment.data?.assignmentCancelation || {}),
            cancelationDate: BaseDateService.format(new Date(), 'yyyy-MM-dd'),
          },
        };
        return;

      case baseAssignmentsActions.updateAssignmentDate.type:
        // @ts-ignore
        const index = (draftState.dates.data.data || []).findIndex((date) => date.id === action.payload.id);
        // @ts-ignore
        draftState.dates.data.data[index] = action.payload;
        draftState.dates.data = {
          ...draftState.dates.data,
          // @ts-ignore
          data: [...draftState.dates.data.data],
        };
        return;

      // Assignment customer
      case baseAssignmentsActions.loadAssignmentCustomer.type:
        draftState.customer.ok = undefined;
        return;

      case baseAssignmentsActions.setAssignmentCustomer.type:
        draftState.customer.data = action.payload;
        draftState.customer.ok = true;
        return;

      case baseAssignmentsActions.errorAssignmentCustomer.type:
        draftState.customer.ok = false;
        return;

      // Sign contract
      case baseAssignmentsActions.clearContractStatus.type:
        draftState.signContractStatus.ok = undefined;
        return;

      case baseAssignmentsActions.signContractSuccess.type:
        draftState.signContractStatus.ok = true;
        return;

      case baseAssignmentsActions.signContractError.type:
        draftState.signContractStatus.ok = false;
        return;

      case baseAssignmentsActions.setAssignmentProfileIDCardView.type:
        draftState.assignmentProfileIDCard.data = action.payload.document;
        draftState.assignmentProfileIDCard.fileName = action.payload.fileName;
        return;

      case baseAssignmentsActions.clearAssignmentProfileIDCardView.type:
        draftState.assignmentProfileIDCard.data = undefined;
        draftState.assignmentProfileIDCard.fileName = undefined;
        return;

      // Work Council Comments
      case baseAssignmentsActions.loadWorkCouncilCommentList.type:
        draftState.workCouncilComments.ok = undefined;
        return;

      case baseAssignmentsActions.setWorkCouncilCommentList.type:
        draftState.workCouncilComments.data = action.payload;
        return;

      case baseAssignmentsActions.successWorkCouncilCommentList.type:
        draftState.workCouncilComments.ok = true;
        return;

      case baseAssignmentsActions.errorWorkCouncilCommentList.type:
        draftState.workCouncilComments.ok = false;
        return;

      case baseAssignmentsActions.clearWorkCouncilCommentList.type:
        draftState.workCouncilComments = ASSIGNMENTS_INITIAL_STATE.workCouncilComments;
        return;

      // Assignments with upcoming EP date
      case baseAssignmentsActions.loadUpcomingEPAssignments.type:
        draftState.upcomingEPAssignments.ok = undefined;
        return;

      case baseAssignmentsActions.setUpcomingEPAssignments.type:
        draftState.upcomingEPAssignments.data = action.payload;
        return;

      case baseAssignmentsActions.successUpcomingEPAssignments.type:
        draftState.upcomingEPAssignments.ok = true;
        return;

      case baseAssignmentsActions.errorUpcomingEPAssignments.type:
        draftState.upcomingEPAssignments.ok = false;
        return;

      // Assignments with upcoming MLP date
      case baseAssignmentsActions.loadUpcomingMLPAssignments.type:
        draftState.upcomingMLPAssignments.ok = undefined;
        return;

      case baseAssignmentsActions.setUpcomingMLPAssignments.type:
        draftState.upcomingMLPAssignments.data = action.payload;
        return;

      case baseAssignmentsActions.successUpcomingMLPAssignments.type:
        draftState.upcomingMLPAssignments.ok = true;
        return;

      case baseAssignmentsActions.errorUpcomingMLPAssignments.type:
        draftState.upcomingMLPAssignments.ok = false;
        return;

      // Location department qualification list
      case baseAssignmentsActions.loadLocationDepartmentQualificationList.type:
        draftState.locationDepartmentQualificationList.ok = undefined;
        return;

      case baseAssignmentsActions.setLocationDepartmentQualificationList.type:
        draftState.locationDepartmentQualificationList.data = action.payload;
        return;

      case baseAssignmentsActions.successLocationDepartmentQualificationList.type:
        draftState.locationDepartmentQualificationList.ok = true;
        return;

      case baseAssignmentsActions.errorLocationDepartmentQualificationList.type:
        draftState.locationDepartmentQualificationList.ok = false;
        return;

      case baseAssignmentsActions.resetLocationDepartmentQualificationList.type:
        draftState.locationDepartmentQualificationList = ASSIGNMENTS_INITIAL_STATE.locationDepartmentQualificationList;
        return;

      // Customer location department
      case baseAssignmentsActions.loadPspCustomerLocationDepartment.type:
        draftState.customerLocationDepartment.ok = undefined;
        return;

      case baseAssignmentsActions.setPspCustomerLocationDepartment.type:
        draftState.customerLocationDepartment.data = action.payload;
        return;

      case baseAssignmentsActions.successPspCustomerLocationDepartment.type:
        draftState.customerLocationDepartment.ok = true;
        return;

      case baseAssignmentsActions.errorPspCustomerLocationDepartment.type:
        draftState.customerLocationDepartment.ok = false;
        return;

      case baseAssignmentsActions.resetPspCustomerLocationDepartment.type:
        draftState.customerLocationDepartment = ASSIGNMENTS_INITIAL_STATE.customerLocationDepartment;
        return;

      // Assignment date list
      case baseAssignmentsActions.loadPspAssignmentDateList.type:
        draftState.assignmentDateList.ok = undefined;
        return;

      case baseAssignmentsActions.setPspAssignmentDateList.type:
        draftState.assignmentDateList.data = action.payload;
        return;

      case baseAssignmentsActions.successPspAssignmentDateList.type:
        draftState.assignmentDateList.ok = true;
        return;

      case baseAssignmentsActions.errorPspAssignmentDateList.type:
        draftState.assignmentDateList.ok = false;
        return;

      case baseAssignmentsActions.resetPspAssignmentDateList.type:
        draftState.assignmentDateList = ASSIGNMENTS_INITIAL_STATE.assignmentDateList;
        return;

      // Assignment date timestamp list
      case baseAssignmentsActions.loadAssignmentDateTimestampList.type:
        draftState.assignmentDateTimestampList.ok = undefined;
        draftState.assignmentDateTimestampList.data = undefined;
        return;

      case baseAssignmentsActions.setAssignmentDateTimestampList.type:
        draftState.assignmentDateTimestampList.data = action.payload;
        return;

      case baseAssignmentsActions.successAssignmentDateTimestampList.type:
        draftState.assignmentDateTimestampList.ok = true;
        return;

      case baseAssignmentsActions.errorAssignmentDateTimestampList.type:
        draftState.assignmentDateTimestampList.ok = false;
        return;

      case baseAssignmentsActions.resetAssignmentDateTimestampList.type:
        draftState.assignmentDateTimestampList = ASSIGNMENTS_INITIAL_STATE.assignmentDateTimestampList;
        return;
    }
  });
}
